import { Duration } from 'dayjs/plugin/duration';

import { assignSafe, FileContentType, isString, SDuration } from '@summit/core';
import { DayOfWeek, isNumber } from '@summit/models';

import { ParameterPolicy } from '../parameter-policy.enum';
import { RoleAccess } from './role-access.enum';

export class DigitalLogParameter {
  //Log Types
  //linehaulEnabled: boolean = null;
  linehaulAccess: RoleAccess = null;
  //spotsEnabled: boolean = null;
  spotsAccess: RoleAccess = null;
  //hourlyEnabled: boolean = null;
  hourlyAccess: RoleAccess = null;
  //expenseEnabled: boolean = null;
  pnDAccess: RoleAccess = null;
  expenseAccess: RoleAccess = null;
  //miscWorkEnabled: boolean = null;
  miscWorkAccess: RoleAccess = null;
  prePostTripAccess: RoleAccess = null;
  //Parameter Policies
  divisionWideDelegation?: boolean = null;
  soloTeamPay?: boolean = null;
  trailerNumbersPolicy?: ParameterPolicy = null;
  dollyNumbersPolicy?: ParameterPolicy = null;
  sealNumbersPolicy?: ParameterPolicy = null;
  odometerBeginPolicy?: ParameterPolicy = null;
  odometerEndPolicy?: ParameterPolicy = null;
  departureDelayPolicy?: ParameterPolicy = null;
  rerouteDelayPolicy?: ParameterPolicy = null;
  routePolicy?: ParameterPolicy = null;
  startTimePolicy?: ParameterPolicy = null;
  endTimePolicy?: ParameterPolicy = null;
  eldPolicy?: ParameterPolicy = null;
  departureTimePolicy?: ParameterPolicy = null;
  arrivalTimePolicy?: ParameterPolicy = null;
  commentPolicy?: ParameterPolicy = null;
  //Cut off times
  driverCutOffDay: DayOfWeek;
  driverCutOffTime: Duration;
  managerCutOffDay: DayOfWeek;
  managerCutOffTime: Duration;
  fileContentTypes: FileContentType[];

  constructor(jsonData?: Partial<DigitalLogParameter>) {
    if (jsonData) {
      //console.trace('division digital log parameters', jsonData.dollyNumbersPolicy, jsonData.sealNumbersPolicy);
      assignSafe(this, jsonData);
      this.linehaulAccess = this.getEnumValue(jsonData.linehaulAccess);
      this.spotsAccess = this.getEnumValue(jsonData.spotsAccess);
      this.hourlyAccess = this.getEnumValue(jsonData.hourlyAccess);
      this.pnDAccess = this.getEnumValue(jsonData.pnDAccess);
      this.expenseAccess = this.getEnumValue(jsonData.expenseAccess);
      this.miscWorkAccess = this.getEnumValue(jsonData.miscWorkAccess);
      this.prePostTripAccess = this.getEnumValue(jsonData.prePostTripAccess);
      this.trailerNumbersPolicy = this.getEnumValue(jsonData.trailerNumbersPolicy);
      this.dollyNumbersPolicy = this.getEnumValue(jsonData.dollyNumbersPolicy);
      this.sealNumbersPolicy = this.getEnumValue(jsonData.sealNumbersPolicy);
      this.odometerBeginPolicy = this.getEnumValue(jsonData.odometerBeginPolicy);
      this.odometerEndPolicy = this.getEnumValue(jsonData.odometerEndPolicy);
      this.departureDelayPolicy = this.getEnumValue(jsonData.departureDelayPolicy);
      this.rerouteDelayPolicy = this.getEnumValue(jsonData.rerouteDelayPolicy);
      this.routePolicy = this.getEnumValue(jsonData.routePolicy);
      this.startTimePolicy = this.getEnumValue(jsonData.startTimePolicy);
      this.endTimePolicy = this.getEnumValue(jsonData.endTimePolicy);
      this.eldPolicy = this.getEnumValue(jsonData.eldPolicy);
      this.departureTimePolicy = this.getEnumValue(jsonData.departureTimePolicy);
      this.arrivalTimePolicy = this.getEnumValue(jsonData.arrivalTimePolicy);
      this.commentPolicy = this.getEnumValue(jsonData.commentPolicy);
      if (jsonData.driverCutOffTime) this.driverCutOffTime = SDuration.new(jsonData.driverCutOffTime);
      if (jsonData.managerCutOffTime) this.managerCutOffTime = SDuration.new(jsonData.managerCutOffTime);
      if (jsonData.fileContentTypes) this.fileContentTypes = [...jsonData.fileContentTypes];
    }
    this.driverCutOffDay ??= DayOfWeek.Tuesday;
    this.driverCutOffTime ??= SDuration.new({ hours: 21 });
    this.managerCutOffDay ??= DayOfWeek.Tuesday;
    this.managerCutOffTime ??= SDuration.new({ hours: 21 });
    this.fileContentTypes ??= [];
    //console.trace('returning', this.routePolicy, this);
  }

  private getEnumValue(value: string | number | null): number {
    if (value === undefined || value === null || (isString(value) && value == '')) return null;
    if (isNumber(value)) return value;
    return parseInt(value);
  }

  isEquivalent(other: DigitalLogParameter): boolean {
    if (!other) return false;
    return (
      this.linehaulAccess === other.linehaulAccess &&
      this.spotsAccess === other.spotsAccess &&
      this.hourlyAccess === other.hourlyAccess &&
      this.pnDAccess === other.pnDAccess &&
      this.expenseAccess === other.expenseAccess &&
      this.miscWorkAccess === other.miscWorkAccess &&
      this.prePostTripAccess === other.prePostTripAccess &&
      this.divisionWideDelegation === other.divisionWideDelegation &&
      this.soloTeamPay === other.soloTeamPay &&
      this.trailerNumbersPolicy === other.trailerNumbersPolicy &&
      this.dollyNumbersPolicy === other.dollyNumbersPolicy &&
      this.sealNumbersPolicy === other.sealNumbersPolicy &&
      this.odometerBeginPolicy === other.odometerBeginPolicy &&
      this.odometerEndPolicy === other.odometerEndPolicy &&
      this.departureDelayPolicy === other.departureDelayPolicy &&
      this.rerouteDelayPolicy === other.rerouteDelayPolicy &&
      this.routePolicy === other.routePolicy &&
      this.driverCutOffDay === other.driverCutOffDay &&
      this.driverCutOffTime.asHours() === other.driverCutOffTime.asHours() &&
      this.managerCutOffDay === other.managerCutOffDay &&
      this.managerCutOffTime.asHours() === other.managerCutOffTime.asHours() &&
      this.startTimePolicy === other.startTimePolicy &&
      this.endTimePolicy === other.endTimePolicy &&
      this.eldPolicy === other.eldPolicy &&
      this.departureTimePolicy === other.departureTimePolicy &&
      this.arrivalTimePolicy === other.arrivalTimePolicy &&
      this.commentPolicy === other.commentPolicy
    );
  }

  setCorporateDefaults() {
    this.linehaulAccess ??= RoleAccess.All;
    this.spotsAccess ??= RoleAccess.All;
    this.hourlyAccess ??= RoleAccess.All;
    this.expenseAccess ??= RoleAccess.All;
    this.miscWorkAccess ??= RoleAccess.All;
    this.pnDAccess ??= RoleAccess.None;
    this.prePostTripAccess ??= RoleAccess.None;
    this.divisionWideDelegation ??= false;
    this.soloTeamPay ??= false;
    this.trailerNumbersPolicy ??= ParameterPolicy.DoNotAsk;
    this.dollyNumbersPolicy ??= ParameterPolicy.DoNotAsk;
    this.sealNumbersPolicy ??= ParameterPolicy.DoNotAsk;
    this.odometerBeginPolicy ??= ParameterPolicy.DoNotAsk;
    this.odometerEndPolicy ??= ParameterPolicy.DoNotAsk;
    this.departureDelayPolicy ??= ParameterPolicy.DoNotAsk;
    this.rerouteDelayPolicy ??= ParameterPolicy.DoNotAsk;
    this.routePolicy ??= ParameterPolicy.AskAndRequire;
    this.startTimePolicy ??= ParameterPolicy.DoNotAsk;
    this.endTimePolicy ??= ParameterPolicy.DoNotAsk;
    this.eldPolicy ??= ParameterPolicy.DoNotAsk;
    this.departureTimePolicy ??= ParameterPolicy.DoNotAsk;
    this.arrivalTimePolicy ??= ParameterPolicy.DoNotAsk;
    this.commentPolicy ??= ParameterPolicy.Ask;
  }

  //TODO add spot Enabled when merged
  get showLogEntry(): boolean {
    return this.linehaulAccess > 0 || this.hourlyAccess > 0;
  }
  get showOdometerBegin(): boolean {
    return (
      this.odometerBeginPolicy === ParameterPolicy.Ask || this.odometerBeginPolicy === ParameterPolicy.AskAndRequire
    );
  }
  get showOdometerEnd(): boolean {
    return this.odometerEndPolicy === ParameterPolicy.Ask || this.odometerEndPolicy === ParameterPolicy.AskAndRequire;
  }
  get showTrailerNumbers(): boolean {
    return (
      this.trailerNumbersPolicy === ParameterPolicy.Ask || this.trailerNumbersPolicy === ParameterPolicy.AskAndRequire
    );
  }
  get showDollyNumbers(): boolean {
    return this.dollyNumbersPolicy === ParameterPolicy.Ask || this.dollyNumbersPolicy === ParameterPolicy.AskAndRequire;
  }
  get showSealNumbers(): boolean {
    return this.sealNumbersPolicy === ParameterPolicy.Ask || this.sealNumbersPolicy === ParameterPolicy.AskAndRequire;
  }
  get showDepartureDelay(): boolean {
    return (
      this.departureDelayPolicy === ParameterPolicy.Ask || this.departureDelayPolicy === ParameterPolicy.AskAndRequire
    );
  }
  get showRerouteDelay(): boolean {
    return this.rerouteDelayPolicy === ParameterPolicy.Ask || this.rerouteDelayPolicy === ParameterPolicy.AskAndRequire;
  }
  get showRoute(): boolean {
    return this.routePolicy === ParameterPolicy.Ask || this.routePolicy === ParameterPolicy.AskAndRequire;
  }
  get showStartTime(): boolean {
    return this.startTimePolicy === ParameterPolicy.Ask || this.startTimePolicy === ParameterPolicy.AskAndRequire;
  }
  get showEndTime(): boolean {
    return this.endTimePolicy === ParameterPolicy.Ask || this.endTimePolicy === ParameterPolicy.AskAndRequire;
  }
  get showEld(): boolean {
    return this.eldPolicy === ParameterPolicy.Ask || this.eldPolicy === ParameterPolicy.AskAndRequire;
  }
  get showDepartureTime(): boolean {
    return (
      this.departureTimePolicy === ParameterPolicy.Ask || this.departureTimePolicy === ParameterPolicy.AskAndRequire
    );
  }
  get showArrivalTime(): boolean {
    return this.arrivalTimePolicy === ParameterPolicy.Ask || this.arrivalTimePolicy === ParameterPolicy.AskAndRequire;
  }
  get showComment(): boolean {
    return this.commentPolicy === ParameterPolicy.Ask || this.commentPolicy === ParameterPolicy.AskAndRequire;
  }
}
